import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Cloud,
  Download,
  FileText,
  FileSpreadsheet,
  History,
  KeyRound,
  Lock,
  LogOut,
  Palmtree,
  RefreshCw,
  RotateCcw,
  Settings,
  ShieldCheck,
  Sparkles,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  automaticBreakMinutes,
  calculateWorkTotals,
  currentJstDate,
  enumerateMonthDates,
  formatDuration,
  formatMinutesAsTime,
  leavePeriodStartForDate,
  type DayRecord,
  type MonthPayload,
  type WorkStatus,
} from "../shared/domain";
import { api, ApiClientError } from "./api";
import { createMonthlyWorkbook, downloadBlob } from "./excel";
import { isJapaneseHoliday } from "./japaneseHolidays";
import { createLeaveRequestWorkbook, groupPaidLeaveRequests } from "./leaveExcel";

declare global {
  interface Window {
      turnstile?: {
        render: (element: HTMLElement, options: { sitekey: string; action: string; callback: (token: string) => void; theme: string }) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const STATUS = {
  work: { label: "勤務", short: "勤", icon: BriefcaseBusiness },
  day_off: { label: "公休", short: "公", icon: Palmtree },
  paid_leave: { label: "有給", short: "有", icon: Sparkles },
  holiday_work: { label: "休日出勤", short: "休出", icon: Clock3 },
} satisfies Record<WorkStatus, { label: string; short: string; icon: typeof BriefcaseBusiness }>;

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];
const TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => index * 15);

function monthKeyFrom(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(monthKey: string, amount: number): string {
  const [year, month] = monthKey.split("-").map(Number);
  return monthKeyFrom(new Date(year, month - 1 + amount, 1));
}

function monthTitle(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  return `${year}年 ${month}月`;
}

function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("ja-JP", { month: "long", day: "numeric", weekday: "short" })
    .format(new Date(`${date}T00:00:00+09:00`));
}

function emptyDay(date: string): DayRecord {
  return {
    workDate: date,
    status: null,
    scheduleText: "",
    plannedStartMinutes: 480,
    plannedEndMinutes: 1020,
    actualStartMinutes: null,
    actualEndMinutes: null,
    breakMinutes: 60,
    breakIsManual: false,
    actualConfirmed: false,
    revision: 0,
    source: "app",
  };
}

function expandedDays(payload: MonthPayload): DayRecord[] {
  const saved = new Map(payload.days.map((day) => [day.workDate, day]));
  return enumerateMonthDates(payload.month.monthKey).map((date) => saved.get(date) ?? emptyDay(date));
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "処理に失敗しました。";
}

interface ToastMessage {
  id: number;
  text: string;
  tone: "success" | "error";
}

function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    const render = () => {
      if (!cancelled && ref.current && window.turnstile) {
        window.turnstile.render(ref.current, { sitekey: siteKey, action: "login", callback: onToken, theme: "light" });
      }
    };
    if (window.turnstile) {
      render();
      return () => { cancelled = true; };
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.append(script);
    return () => { cancelled = true; };
  }, [onToken, siteKey]);
  return siteKey ? <div className="turnstile-slot" ref={ref} /> : <p className="field-note">ローカル開発では追加確認を自動で通過します。</p>;
}

function LoginScreen({ siteKey, onAuthenticated }: { siteKey: string; onAuthenticated: () => void }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsTurnstile, setNeedsTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!/^\d{4}$/u.test(pin)) {
      setError("4桁の数字を入力してください。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/login", { pin, turnstileToken: turnstileToken || undefined });
      onAuthenticated();
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.details && typeof caught.details === "object") {
        const details = caught.details as { needsTurnstile?: boolean; lockedUntil?: number; remainingAttempts?: number };
        setNeedsTurnstile(Boolean(details.needsTurnstile));
        if (details.lockedUntil) {
          setError(`ログインは${new Date(details.lockedUntil * 1000).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}まで停止中です。`);
        } else if (typeof details.remainingAttempts === "number") {
          setError(`${caught.message} 残り${details.remainingAttempts}回です。`);
        } else {
          setError(caught.message);
        }
      } else {
        setError(getErrorMessage(caught));
      }
      setTurnstileToken("");
      window.turnstile?.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <div className="brand-mark"><CalendarDays size={28} /></div>
        <p className="eyebrow">PRIVATE WORKSPACE</p>
        <h1 id="login-title">nobuスケジュール</h1>
        <p className="login-lead">勤務と休みを、毎日ひと目で。</p>
        <form onSubmit={submit}>
          <label className="field-label" htmlFor="pin">4桁PIN</label>
          <div className="pin-wrap">
            <KeyRound size={20} aria-hidden="true" />
            <input
              id="pin"
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
              maxLength={4}
              pattern="[0-9]{4}"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/gu, "").slice(0, 4))}
              placeholder="••••"
              autoFocus
            />
          </div>
          {needsTurnstile && <Turnstile siteKey={siteKey} onToken={setTurnstileToken} />}
          {error && <div className="inline-error" role="alert"><AlertCircle size={17} />{error}</div>}
          <button className="primary-button login-button" type="submit" disabled={busy || (needsTurnstile && Boolean(siteKey) && !turnstileToken)}>
            {busy ? <RefreshCw className="spin" size={18} /> : <ShieldCheck size={18} />}
            ログイン
          </button>
        </form>
        <p className="secure-note"><Lock size={14} />30日後に自動でログアウトします</p>
      </section>
    </main>
  );
}

function SummaryCard({ label, value, detail, icon: Icon, tone }: {
  label: string; value: string; detail: string; icon: typeof Clock3; tone: string;
}) {
  return (
    <article className={`summary-card ${tone}`}>
      <div className="summary-icon"><Icon size={20} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

function CalendarCell({ day, today, onOpen }: { day: DayRecord; today: string; onOpen: () => void }) {
  const date = new Date(`${day.workDate}T00:00:00+09:00`);
  const isToday = day.workDate === today;
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const isHoliday = isJapaneseHoliday(day.workDate);
  const totals = calculateWorkTotals(day);
  const meta = day.status ? STATUS[day.status] : null;
  return (
    <button
      className={`calendar-cell ${meta ? `status-${day.status}` : "status-empty"} ${isToday ? "is-today" : ""} ${isHoliday ? "is-holiday" : ""}`}
      onClick={onOpen}
      aria-label={`${dayLabel(day.workDate)} ${meta?.label ?? "未設定"}`}
    >
      <span className={`day-number ${isWeekend ? "weekend" : ""} ${isHoliday ? "holiday" : ""}`}>{date.getDate()}</span>
      {meta ? <span className="status-pill">{day.status === "holiday_work" ? "休出" : meta.label}</span> : <span className="empty-dot" />}
      {(day.status === "work" || day.status === "holiday_work") && (
        <span className={`cell-time ${day.actualConfirmed ? "confirmed" : "draft"}`}>
          {day.actualStartMinutes !== null && day.actualEndMinutes !== null
            ? <span className="time-range"><span>{formatMinutesAsTime(day.actualStartMinutes)}</span><span className="time-divider">–</span><span>{formatMinutesAsTime(day.actualEndMinutes)}</span></span>
            : "時間未入力"}
          {day.actualConfirmed ? <Check size={12} /> : <AlertCircle size={12} />}
        </span>
      )}
      {(day.scheduleText || !meta) && <span className="cell-content">{day.scheduleText || "未設定"}</span>}
      {totals.overtimeMinutes > 0 && <span className="overtime-mini">残 {totals.overtimeMinutes / 60}h</span>}
    </button>
  );
}

function TimeSelect({ value, onChange, id }: { value: number | null; onChange: (value: number | null) => void; id: string }) {
  return (
    <select id={id} value={value ?? ""} onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}>
      <option value="">未入力</option>
      {TIME_OPTIONS.map((minutes) => <option key={minutes} value={minutes}>{formatMinutesAsTime(minutes)}</option>)}
    </select>
  );
}

function DayEditor({ initial, finalized, onClose, onSaved, notify }: {
  initial: DayRecord;
  finalized: boolean;
  onClose: () => void;
  onSaved: (day: DayRecord) => void;
  notify: (text: string, tone?: ToastMessage["tone"]) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [previousWorkValues, setPreviousWorkValues] = useState<Pick<DayRecord, "actualStartMinutes" | "actualEndMinutes" | "breakMinutes" | "actualConfirmed"> | null>(null);
  const month = Number(initial.workDate.slice(5, 7));
  const workLike = draft.status === "work" || draft.status === "holiday_work";
  const totals = calculateWorkTotals(draft);

  function chooseStatus(status: WorkStatus) {
    if (status === "work" && draft.status !== "work") {
      setDraft({
        ...draft,
        status,
        actualStartMinutes: 480,
        actualEndMinutes: 1020,
        breakMinutes: 60,
        breakIsManual: false,
        actualConfirmed: true,
      });
      return;
    }
    const wasWork = draft.status === "work" || draft.status === "holiday_work";
    const nextWork = status === "work" || status === "holiday_work";
    if (wasWork && !nextWork && (draft.actualStartMinutes !== null || draft.actualEndMinutes !== null)) {
      if (!window.confirm("入力済みの勤務時間を削除しますか？")) return;
      setPreviousWorkValues({
        actualStartMinutes: draft.actualStartMinutes,
        actualEndMinutes: draft.actualEndMinutes,
        breakMinutes: draft.breakMinutes,
        actualConfirmed: draft.actualConfirmed,
      });
      setDraft({ ...draft, status, actualStartMinutes: null, actualEndMinutes: null, actualConfirmed: false });
      return;
    }
    if (!wasWork && nextWork) {
      const summer = month >= 4 && month <= 11;
      setDraft({
        ...draft,
        status,
        actualStartMinutes: summer ? 480 : null,
        actualEndMinutes: summer ? 1020 : null,
        breakMinutes: 60,
        breakIsManual: false,
        actualConfirmed: false,
      });
      return;
    }
    setDraft({ ...draft, status });
  }

  function setTime(key: "actualStartMinutes" | "actualEndMinutes", value: number | null) {
    const next = { ...draft, [key]: value, actualConfirmed: false };
    if (!next.breakIsManual) next.breakMinutes = automaticBreakMinutes(next.actualStartMinutes, next.actualEndMinutes);
    setDraft(next);
  }

  async function save() {
    if (finalized) return;
    setBusy(true);
    try {
      const saved = await api.put<DayRecord>(`/api/days/${draft.workDate}`, draft);
      onSaved(saved);
      notify("保存しました。", "success");
      onClose();
    } catch (error) {
      notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="day-sheet" role="dialog" aria-modal="true" aria-labelledby="day-editor-title">
        <div className="sheet-handle" />
        <header className="sheet-header">
          <div>
            <p className="eyebrow">DAY DETAILS</p>
            <h2 id="day-editor-title">{dayLabel(draft.workDate)}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="閉じる"><X /></button>
        </header>

        {finalized && <div className="locked-banner"><Lock size={16} />確定済みの月です。編集するには確定解除してください。</div>}

        <div className="editor-scroll">
          <fieldset disabled={finalized}>
            <legend>勤務区分</legend>
            <div className="status-options">
              {(Object.keys(STATUS) as WorkStatus[]).map((status) => {
                const item = STATUS[status];
                const Icon = item.icon;
                return (
                  <button key={status} type="button" className={`status-option status-${status} ${draft.status === status ? "selected" : ""}`} onClick={() => chooseStatus(status)}>
                    <Icon size={18} />{item.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {previousWorkValues && !workLike && !finalized && (
            <button className="undo-button" onClick={() => {
              setDraft({ ...draft, status: "work", ...previousWorkValues });
              setPreviousWorkValues(null);
            }}><Undo2 size={16} />勤務時間の削除を元に戻す</button>
          )}

          <label className="field-label" htmlFor="schedule-text">{draft.status === "paid_leave" ? "休暇理由" : "スケジュール内容"}</label>
          <textarea
            id="schedule-text"
            value={draft.scheduleText}
            onChange={(event) => setDraft({ ...draft, scheduleText: event.target.value })}
            maxLength={500}
            placeholder={draft.status === "paid_leave" ? "例：私用のため、通院のため" : "例：バス運行管理、車両点検"}
            disabled={finalized}
          />

          <div className="planned-strip">
            <span><CalendarDays size={16} />予定時間</span>
            <strong>8:00–17:00</strong>
          </div>

          {workLike && (
            <div className="actual-panel">
              <div className="section-heading">
                <div><p className="eyebrow">ACTUAL</p><h3>勤務実績</h3></div>
                {!draft.actualConfirmed && <span className="draft-badge"><AlertCircle size={13} />未確認</span>}
              </div>
              <div className="time-grid">
                <label htmlFor="start-time">始業<TimeSelect id="start-time" value={draft.actualStartMinutes} onChange={(value) => setTime("actualStartMinutes", value)} /></label>
                <label htmlFor="end-time">終業<TimeSelect id="end-time" value={draft.actualEndMinutes} onChange={(value) => setTime("actualEndMinutes", value)} /></label>
              </div>
              {month >= 12 || month <= 3 ? (
                <button className="secondary-button compact" type="button" onClick={() => setDraft({ ...draft, actualStartMinutes: 480, actualEndMinutes: 1020, breakMinutes: 60, breakIsManual: false, actualConfirmed: false })}>
                  <Clock3 size={16} />予定時間をコピー
                </button>
              ) : null}
              <div className="break-row">
                <div><span>休憩時間</span><small>{draft.breakIsManual ? "手動設定" : "勤務時間から自動判定"}</small></div>
                <div className="segmented-mini">
                  {[60, 90].map((value) => <button key={value} type="button" className={draft.breakMinutes === value ? "active" : ""} onClick={() => setDraft({ ...draft, breakMinutes: value as 60 | 90, breakIsManual: true, actualConfirmed: false })}>{value === 60 ? "1時間" : "1.5時間"}</button>)}
                </div>
              </div>
              {draft.breakIsManual && <button className="text-button" type="button" onClick={() => setDraft({ ...draft, breakMinutes: automaticBreakMinutes(draft.actualStartMinutes, draft.actualEndMinutes), breakIsManual: false, actualConfirmed: false })}><RotateCcw size={14} />自動判定に戻す</button>}
              <div className="calculation-preview">
                <div><span>実働</span><strong>{formatDuration(totals.workedMinutes)}</strong></div>
                <div><span>残業</span><strong>{formatDuration(totals.overtimeMinutes)}</strong></div>
              </div>
              <button
                type="button"
                className={`confirm-actual ${draft.actualConfirmed ? "confirmed" : ""}`}
                disabled={draft.actualStartMinutes === null || draft.actualEndMinutes === null}
                onClick={() => setDraft({ ...draft, actualConfirmed: !draft.actualConfirmed })}
              >
                <CheckCircle2 size={19} />{draft.actualConfirmed ? "実績確認済み" : "この時間を実績として確認"}
              </button>
            </div>
          )}
        </div>
        <footer className="sheet-footer">
          <button className="secondary-button" onClick={onClose}>キャンセル</button>
          <button className="primary-button" onClick={save} disabled={busy || finalized}>{busy ? <RefreshCw className="spin" size={17} /> : <Check size={17} />}保存</button>
        </footer>
      </section>
    </div>
  );
}

function SettingsPanel({ payload, onClose, onReload, notify, onLoggedOut }: {
  payload: MonthPayload;
  onClose: () => void;
  onReload: () => Promise<void>;
  notify: (text: string, tone?: ToastMessage["tone"]) => void;
  onLoggedOut: () => void;
}) {
  const [required, setRequired] = useState(payload.month.requiredDaysOff?.toString() ?? "");
  const activePeriodStart = leavePeriodStartForDate(`${payload.month.monthKey}-15`);
  const activePeriod = payload.leavePeriods.find((period) => period.periodStart === activePeriodStart);
  const [granted, setGranted] = useState(activePeriod?.grantedDays?.toString() ?? "");
  const [startingUsed, setStartingUsed] = useState(activePeriod?.startingUsedDays?.toString() ?? "");
  const [displayName, setDisplayName] = useState(payload.profile.displayName);
  const [workplace, setWorkplace] = useState(payload.profile.workplace);
  const [leaveRecipient, setLeaveRecipient] = useState(payload.profile.leaveRecipient);
  const [leaveOrganization, setLeaveOrganization] = useState(payload.profile.leaveOrganization);
  const [leaveDepartment, setLeaveDepartment] = useState(payload.profile.leaveDepartment);
  const [busy, setBusy] = useState(false);
  const restoreInput = useRef<HTMLInputElement>(null);

  async function saveSettings() {
    if (required === "") {
      notify("必要公休数を入力してください。", "error");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/api/months/${payload.month.monthKey}/settings`, {
        requiredDaysOff: Number(required),
        revision: payload.month.revision,
      });
      await api.put("/api/leave-periods", {
        periodStart: activePeriodStart,
        grantedDays: granted === "" ? null : Number(granted),
        startingUsedDays: startingUsed === "" ? null : Number(startingUsed),
        revision: activePeriod?.revision ?? 0,
      });
      await api.put("/api/profile", {
        displayName,
        workplace,
        leaveRecipient,
        leaveOrganization,
        leaveDepartment,
      });
      await onReload();
      notify("設定を保存しました。", "success");
      onClose();
    } catch (error) {
      notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  async function downloadBackup() {
    try {
      const response = await fetch("/api/backup", { credentials: "same-origin" });
      if (!response.ok) throw new Error("バックアップを取得できませんでした。");
      downloadBlob(await response.blob(), `nobu-schedule-backup-${currentJstDate()}.json`);
      notify("バックアップを保存しました。", "success");
    } catch (error) {
      notify(getErrorMessage(error), "error");
    }
  }

  async function restore(file: File) {
    try {
      const backup = JSON.parse(await file.text()) as { days?: unknown[]; monthSettings?: unknown[]; leavePeriods?: unknown[] };
      const summary = `日別${backup.days?.length ?? 0}件、月設定${backup.monthSettings?.length ?? 0}件、有給設定${backup.leavePeriods?.length ?? 0}件で現在のデータを置き換えます。続けますか？`;
      if (!window.confirm(summary)) return;
      setBusy(true);
      await api.post("/api/restore", backup);
      await onReload();
      notify("バックアップを復元しました。", "success");
      onClose();
    } catch (error) {
      notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  async function logout(all: boolean) {
    try {
      await api.post(all ? "/api/auth/logout-all" : "/api/auth/logout");
      onLoggedOut();
    } catch (error) {
      notify(getErrorMessage(error), "error");
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="settings-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <header className="sheet-header">
          <div><p className="eyebrow">SETTINGS</p><h2 id="settings-title">設定とバックアップ</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="閉じる"><X /></button>
        </header>
        <div className="settings-scroll">
          <section className="settings-section">
            <h3>{monthTitle(payload.month.monthKey)}の公休</h3>
            <label className="field-label" htmlFor="required-days">必要公休数</label>
            <div className="number-input"><input id="required-days" type="number" min="0" max="31" value={required} onChange={(event) => setRequired(event.target.value)} /><span>日</span></div>
          </section>
          <section className="settings-section">
            <h3>有給年度</h3>
            <p className="section-note">{activePeriodStart.replaceAll("-", "/")} ～ {Number(activePeriodStart.slice(0, 4)) + 1}/02/14</p>
            <div className="two-fields">
              <label>付与数<div className="number-input"><input type="number" min="0" max="100" value={granted} onChange={(event) => setGranted(event.target.value)} /><span>日</span></div></label>
              <label>開始時の使用数<div className="number-input"><input type="number" min="0" max="100" value={startingUsed} onChange={(event) => setStartingUsed(event.target.value)} /><span>日</span></div></label>
            </div>
          </section>
          <section className="settings-section">
            <h3>提出書類</h3>
            <p className="section-note">勤務表と休暇届に印字する内容です。</p>
            <div className="form-fields">
              <label>氏名<input maxLength={100} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
              <label>勤務表の職場名<input maxLength={100} value={workplace} onChange={(event) => setWorkplace(event.target.value)} /></label>
              <label>休暇届の宛先<input maxLength={100} value={leaveRecipient} onChange={(event) => setLeaveRecipient(event.target.value)} /></label>
              <label>休暇届の会社名<input maxLength={100} value={leaveOrganization} onChange={(event) => setLeaveOrganization(event.target.value)} /></label>
              <label>休暇届の所属<input maxLength={100} value={leaveDepartment} onChange={(event) => setLeaveDepartment(event.target.value)} /></label>
            </div>
          </section>
          <section className="settings-section">
            <h3>データ保護</h3>
            <div className="settings-actions">
              <button className="secondary-button wide" onClick={downloadBackup}><Download size={17} />JSONバックアップを保存</button>
              <button className="secondary-button wide" onClick={() => restoreInput.current?.click()}><Upload size={17} />バックアップから復元</button>
              <input ref={restoreInput} type="file" accept="application/json,.json" hidden onChange={(event) => event.target.files?.[0] && void restore(event.target.files[0])} />
            </div>
          </section>
          <section className="settings-section danger-zone">
            <h3>ログイン</h3>
            <button className="text-action" onClick={() => void logout(false)}><LogOut size={17} />この端末からログアウト</button>
            <button className="text-action danger" onClick={() => window.confirm("すべての端末をログアウトしますか？") && void logout(true)}><ShieldCheck size={17} />すべての端末からログアウト</button>
          </section>
        </div>
        <footer className="sheet-footer"><button className="secondary-button" onClick={onClose}>閉じる</button><button className="primary-button" onClick={saveSettings} disabled={busy}><Check size={17} />設定を保存</button></footer>
      </section>
    </div>
  );
}

function ExportPanel({ payload, days, onClose, onReload, notify }: {
  payload: MonthPayload;
  days: DayRecord[];
  onClose: () => void;
  onReload: () => Promise<void>;
  notify: (text: string, tone?: ToastMessage["tone"]) => void;
}) {
  const [includeSchedule, setIncludeSchedule] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = payload.today;
  const pastOrTodayIssues = days.filter((day) => {
    if (day.workDate >= today) return false;
    return (day.status === "work" || day.status === "holiday_work") && !day.actualConfirmed;
  });
  const missingStatus = days.filter((day) => day.workDate < today && !day.status);
  const dayOffCount = days.filter((day) => day.status === "day_off").length;
  const holidayWorkCount = days.filter((day) => day.status === "holiday_work").length;
  const shortage = payload.month.requiredDaysOff === null ? null : Math.max(0, payload.month.requiredDaysOff - dayOffCount);
  const mismatch = shortage !== null && shortage !== holidayWorkCount;
  const totals = days.reduce((sum, day) => {
    if (!day.actualConfirmed) return sum;
    const current = calculateWorkTotals(day);
    return { workedMinutes: sum.workedMinutes + current.workedMinutes, overtimeMinutes: sum.overtimeMinutes + current.overtimeMinutes };
  }, { workedMinutes: 0, overtimeMinutes: 0 });
  const blockers = [...pastOrTodayIssues, ...missingStatus];
  const leaveRequests = groupPaidLeaveRequests(days);
  const paidLeaveDays = days.filter((day) => day.status === "paid_leave");
  const missingLeaveReasons = paidLeaveDays.filter((day) => !day.scheduleText.trim());

  async function download() {
    if (blockers.length) return;
    setBusy(true);
    try {
      const blob = await createMonthlyWorkbook({ monthKey: payload.month.monthKey, profile: payload.profile, days, includeSchedule });
      const [year, month] = payload.month.monthKey.split("-");
      downloadBlob(blob, `勤務表_${year}年${month}月.xlsx`);
      await api.post(`/api/months/${payload.month.monthKey}/exported`);
      await onReload();
      notify("Excelをダウンロードしました。", "success");
    } catch (error) {
      notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  async function downloadLeaveExcel() {
    if (!leaveRequests.length || missingLeaveReasons.length) return;
    setBusy(true);
    try {
      const { blob } = await createLeaveRequestWorkbook({ profile: payload.profile, days, applicationDate: currentJstDate() });
      const [year, month] = payload.month.monthKey.split("-");
      downloadBlob(blob, `休暇届_${year}年${month}月.xlsx`);
      notify(`休暇届Excelを${leaveRequests.length}シートで作成しました。`, "success");
    } catch (error) {
      notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  async function finalize() {
    if (!window.confirm("この月を確定しますか？確定後は解除するまで編集できません。")) return;
    setBusy(true);
    try {
      await api.post(`/api/months/${payload.month.monthKey}/finalize`);
      await onReload();
      notify("月を確定しました。", "success");
      onClose();
    } catch (error) {
      if (error instanceof ApiClientError && Array.isArray((error.details as { details?: unknown })?.details)) {
        notify(error.message, "error");
      } else notify(getErrorMessage(error), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="export-panel" role="dialog" aria-modal="true" aria-labelledby="export-title">
        <header className="sheet-header">
          <div><p className="eyebrow">MONTHLY REPORT</p><h2 id="export-title">月次出力</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="閉じる"><X /></button>
        </header>
        <div className="export-scroll">
          <div className="flow-steps"><span className="done"><Check />入力</span><i /><span className="current">2</span><i /><span>3</span></div>
          <div className="preview-heading"><div><span>出力対象</span><strong>{monthTitle(payload.month.monthKey)}</strong></div><FileSpreadsheet size={34} /></div>
          <div className="preview-grid">
            <div><span>実働合計</span><strong>{formatDuration(totals.workedMinutes)}</strong></div>
            <div><span>残業合計</span><strong>{formatDuration(totals.overtimeMinutes)}</strong></div>
            <div><span>公休</span><strong>{dayOffCount}日</strong></div>
            <div><span>休日出勤</span><strong>{holidayWorkCount}日</strong></div>
          </div>
          {(blockers.length > 0 || mismatch || payload.month.requiredDaysOff === null) && (
            <div className="issue-box">
              <h3><AlertCircle size={18} />確認が必要です</h3>
              {payload.month.requiredDaysOff === null && <p>必要公休数が未設定です。</p>}
              {missingStatus.length > 0 && <p>過去の未分類日が{missingStatus.length}日あります。</p>}
              {pastOrTodayIssues.length > 0 && <p>実績未確認の勤務日が{pastOrTodayIssues.length}日あります。</p>}
              {mismatch && <p>公休不足{shortage}日に対し、休日出勤が{holidayWorkCount}日です。</p>}
            </div>
          )}
          <label className="toggle-row">
            <input type="checkbox" checked={includeSchedule} onChange={(event) => setIncludeSchedule(event.target.checked)} />
            <span><strong>スケジュール内容もI列へ入れる</strong><small>オフの場合、勤務日の仕事内容欄は空欄です。</small></span>
          </label>
          <div className="excel-note"><FileSpreadsheet size={18} /><p>元ファイルと同じ書式で、計算済みの数値を入力します。公休・有給・休日出勤はI列へ表示します。</p></div>
          <div className="excel-note leave-note"><FileText size={18} /><p>有給は、元の休暇届と同じExcel書式へ入力します。同じ理由の連続日は1シートにまとめます（{leaveRequests.length}シート）。</p></div>
          {missingLeaveReasons.length > 0 && <div className="issue-box compact-issue"><p>休暇理由が未入力の有給日：{missingLeaveReasons.map((day) => Number(day.workDate.slice(8))).join("、")}日</p></div>}
          {payload.month.lastExportedAt && <p className="last-export"><History size={14} />前回出力：{new Date(payload.month.lastExportedAt).toLocaleString("ja-JP")}</p>}
        </div>
        <footer className="export-footer">
          <button className="secondary-button" onClick={onClose}>戻る</button>
          <div>
            {!payload.month.finalizedAt && <button className="secondary-button" onClick={() => void finalize()} disabled={busy}>月を確定</button>}
            <button className="secondary-button" onClick={() => void downloadLeaveExcel()} disabled={busy || !leaveRequests.length || missingLeaveReasons.length > 0}><FileText size={17} />休暇届Excel</button>
            <button className="primary-button" onClick={() => void download()} disabled={busy || blockers.length > 0}>{busy ? <RefreshCw className="spin" size={17} /> : <Download size={17} />}勤務表Excel</button>
          </div>
        </footer>
      </section>
    </div>
  );
}

export default function App() {
  const [sessionChecked, setSessionChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [siteKey, setSiteKey] = useState("");
  const [monthKey, setMonthKey] = useState(monthKeyFrom(new Date()));
  const [payload, setPayload] = useState<MonthPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<DayRecord | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const notify = useCallback((text: string, tone: ToastMessage["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, text, tone }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4200);
  }, []);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<MonthPayload>(`/api/month?month=${monthKey}`);
      setPayload(data);
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 401) setAuthenticated(false);
      else notify(getErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  }, [monthKey, notify]);

  useEffect(() => {
    void api.get<{ authenticated: boolean; turnstileSiteKey: string }>("/api/auth/session")
      .then((session) => {
        setAuthenticated(session.authenticated);
        setSiteKey(session.turnstileSiteKey || "");
      })
      .catch(() => setAuthenticated(false))
      .finally(() => setSessionChecked(true));
  }, []);

  useEffect(() => {
    if (authenticated) void loadMonth();
  }, [authenticated, loadMonth]);

  const days = useMemo(() => payload ? expandedDays(payload) : [], [payload]);
  const calendarSlots = useMemo(() => {
    if (!payload) return [];
    const first = new Date(`${payload.month.monthKey}-01T00:00:00+09:00`);
    const sundayOffset = first.getDay();
    return [...Array<DayRecord | null>(sundayOffset).fill(null), ...days];
  }, [days, payload]);

  const summary = useMemo(() => {
    if (!payload) return null;
    const usedOff = days.filter((day) => day.status === "day_off" && day.workDate < payload.today).length;
    const plannedOff = days.filter((day) => day.status === "day_off" && day.workDate >= payload.today).length;
    const required = payload.month.requiredDaysOff;
    const remaining = required === null ? null : Math.max(0, required - usedOff - plannedOff);
    const overtime = days.reduce((sum, day) => sum + (day.actualConfirmed ? calculateWorkTotals(day).overtimeMinutes : 0), 0);
    const unconfirmed = days.filter((day) => day.workDate < payload.today && (day.status === "work" || day.status === "holiday_work") && !day.actualConfirmed).length;
    const leave = payload.leaveSummary;
    const leaveRemaining = leave.grantedDays === null ? null : leave.grantedDays - leave.usedDays - leave.plannedDays;
    return { usedOff, plannedOff, required, remaining, overtime, unconfirmed, leave, leaveRemaining };
  }, [days, payload]);

  async function unlock() {
    if (!payload || !window.confirm("確定を解除して編集可能にしますか？")) return;
    try {
      await api.post(`/api/months/${payload.month.monthKey}/unlock`);
      await loadMonth();
      notify("確定を解除しました。", "success");
    } catch (error) {
      notify(getErrorMessage(error), "error");
    }
  }

  if (!sessionChecked) return <div className="app-loader"><div className="brand-mark"><CalendarDays /></div><RefreshCw className="spin" /></div>;
  if (!authenticated) return <LoginScreen siteKey={siteKey} onAuthenticated={() => setAuthenticated(true)} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand"><div className="brand-mark small"><CalendarDays size={20} /></div><div><strong>nobuスケジュール</strong><span><Cloud size={12} />同期中</span></div></div>
        <div className="top-actions">
          <button className="icon-button" onClick={() => setSettingsOpen(true)} aria-label="設定"><Settings /></button>
          <div className="avatar">N</div>
        </div>
      </header>

      <main className="dashboard">
        <section className="dashboard-heading">
          <div><p className="eyebrow">MONTHLY OVERVIEW</p><h1>{monthTitle(monthKey)}</h1><p>{payload?.profile.displayName || "勤務スケジュール"}さんの勤務状況</p></div>
          <div className="month-nav">
            <button className="icon-button" onClick={() => setMonthKey(shiftMonth(monthKey, -1))} aria-label="前月"><ArrowLeft /></button>
            <button className="today-button" onClick={() => setMonthKey(currentJstDate().slice(0, 7))}>今月</button>
            <button className="icon-button" onClick={() => setMonthKey(shiftMonth(monthKey, 1))} aria-label="翌月"><ArrowRight /></button>
          </div>
        </section>

        {payload?.month.finalizedAt && <div className="finalized-banner"><div><Lock size={18} /><span><strong>確定済み</strong>{new Date(payload.month.finalizedAt).toLocaleString("ja-JP")}</span></div><button onClick={() => void unlock()}>確定解除</button></div>}

        {summary && (
          <section className="summary-grid" aria-label="月間集計">
            <SummaryCard label="公休" value={summary.required === null ? "未設定" : `${summary.usedOff + summary.plannedOff} / ${summary.required}日`} detail={summary.required === null ? "必要数を設定してください" : `済${summary.usedOff}・予定${summary.plannedOff}・残り${summary.remaining}`} icon={Palmtree} tone="off" />
            <SummaryCard label="有給" value={summary.leave.grantedDays === null ? "未設定" : `${summary.leave.usedDays} / ${summary.leave.grantedDays}日`} detail={summary.leave.grantedDays === null ? "2月15日の付与数を設定" : `予定${summary.leave.plannedDays}・残り見込${summary.leaveRemaining}`} icon={Sparkles} tone="leave" />
            <SummaryCard label="残業" value={formatDuration(summary.overtime)} detail={summary.unconfirmed ? `未確認の勤務 ${summary.unconfirmed}日` : "すべて確認済み"} icon={Clock3} tone="overtime" />
          </section>
        )}

        <section className="content-grid">
          <div className="calendar-card">
            <div className="calendar-toolbar">
              <div className="legend">
                {(Object.keys(STATUS) as WorkStatus[]).map((status) => <span key={status} className={`legend-${status}`}><i />{STATUS[status].label}</span>)}
              </div>
              {loading && <span className="loading-label"><RefreshCw className="spin" size={14} />更新中</span>}
            </div>
            <div className="weekday-row">{WEEKDAYS.map((day) => <span key={day}>{day}</span>)}</div>
            <div className="calendar-grid">
              {calendarSlots.map((day, index) => day ? <CalendarCell key={day.workDate} day={day} today={payload?.today ?? ""} onOpen={() => setSelected(day)} /> : <div className="calendar-blank" key={`blank-${index}`} />)}
            </div>
          </div>

          <aside className="side-panel">
            <div className="side-card action-card">
              <div className="side-card-title"><div className="side-icon"><FileSpreadsheet size={19} /></div><div><span>月次提出</span><strong>勤務表を出力</strong></div></div>
              <p>勤務表Excelと、有給日の休暇届Excelを作成します。</p>
              <button className="primary-button wide" onClick={() => setExportOpen(true)}>確認して出力<ChevronRight size={17} /></button>
              {payload?.month.lastExportedAt && <small>最終出力 {new Date(payload.month.lastExportedAt).toLocaleDateString("ja-JP")}</small>}
            </div>
            <div className="side-card">
              <div className="side-card-title"><div className="side-icon muted"><AlertCircle size={19} /></div><div><span>入力状況</span><strong>{summary?.unconfirmed ? `${summary.unconfirmed}件の確認待ち` : "問題ありません"}</strong></div></div>
              <ul className="check-list">
                <li className={summary?.unconfirmed ? "warn" : "ok"}>{summary?.unconfirmed ? <AlertCircle /> : <CheckCircle2 />}過去の勤務実績</li>
                <li className={payload?.month.requiredDaysOff === null ? "warn" : "ok"}>{payload?.month.requiredDaysOff === null ? <AlertCircle /> : <CheckCircle2 />}必要公休数</li>
                <li className={payload?.leaveSummary.grantedDays === null ? "warn" : "ok"}>{payload?.leaveSummary.grantedDays === null ? <AlertCircle /> : <CheckCircle2 />}有給付与数</li>
              </ul>
            </div>
          </aside>
        </section>
      </main>

      <nav className="mobile-nav" aria-label="主要操作">
        <button className="active" onClick={() => setMonthKey(currentJstDate().slice(0, 7))}><CalendarDays />カレンダー</button>
        <button onClick={() => setExportOpen(true)}><FileSpreadsheet />出力</button>
        <button onClick={() => setSettingsOpen(true)}><Settings />設定</button>
      </nav>

      {selected && payload && <DayEditor initial={selected} finalized={Boolean(payload.month.finalizedAt)} onClose={() => setSelected(null)} onSaved={(saved) => setPayload({ ...payload, days: [...payload.days.filter((day) => day.workDate !== saved.workDate), saved] })} notify={notify} />}
      {settingsOpen && payload && <SettingsPanel payload={payload} onClose={() => setSettingsOpen(false)} onReload={loadMonth} notify={notify} onLoggedOut={() => { setAuthenticated(false); setPayload(null); setSettingsOpen(false); }} />}
      {exportOpen && payload && <ExportPanel payload={payload} days={days} onClose={() => setExportOpen(false)} onReload={loadMonth} notify={notify} />}
      <div className="toast-stack" aria-live="polite">{toasts.map((toast) => <div key={toast.id} className={`toast ${toast.tone}`}>{toast.tone === "success" ? <CheckCircle2 /> : <AlertCircle />}{toast.text}</div>)}</div>
    </div>
  );
}
